import { IonButton, IonIcon } from "@ionic/react";
import { closeOutline, megaphoneOutline } from "ionicons/icons";
import React, { useEffect } from "react";
import { useAnnouncements } from "../data/store/useAnnouncements";
import { useProfile } from "../data/store/useProfile";
import { renderInlineLinks } from "../util/renderInlineLinks";

/**
 * Renders a stack of active site-wide announcements at the top of
 * whichever page mounts it (currently the home / songs feed). Each
 * announcement is dismissible per-user (unless the admin marked it
 * non-dismissible). Expired announcements are filtered out client-side
 * by the store's `active()` selector.
 */
const AnnouncementBanner: React.FC = () => {
  const { ownerId, authenticated } = useProfile();
  const observe = useAnnouncements((s) => s.observe);
  const dismiss = useAnnouncements((s) => s.dismiss);
  // Subscribe to `all` + `dismissedIds` so this component re-renders
  // when either changes; then compute `active` from current state.
  const all = useAnnouncements((s) => s.all);
  const dismissedIds = useAnnouncements((s) => s.dismissedIds);

  useEffect(() => {
    if (!authenticated) return;
    const off = observe(ownerId);
    return off;
  }, [authenticated, ownerId, observe]);

  if (!authenticated) return null;

  const now = Date.now();
  const active = all
    .filter((a) => a.expiresAt > now && !dismissedIds.has(a.id))
    .sort((a, b) => b.createdAt - a.createdAt);

  if (active.length === 0) return null;

  return (
    <div className="announcement-banner-stack">
      {active.map((a) => (
        <div key={a.id} className="announcement-banner" role="status">
          <IonIcon
            icon={megaphoneOutline}
            className="announcement-banner-icon"
            aria-hidden="true"
          />
          <div className="announcement-banner-body">
            {a.title && (
              <div className="announcement-banner-title">{a.title}</div>
            )}
            <div className="announcement-banner-text">
              {renderInlineLinks(a.body)}
            </div>
          </div>
          {a.dismissible && ownerId && (
            <IonButton
              fill="clear"
              size="small"
              onClick={() => dismiss(ownerId, a.id)}
              aria-label={`Dismiss announcement: ${a.title}`}
              className="announcement-banner-dismiss"
            >
              <IonIcon icon={closeOutline} aria-hidden="true" />
            </IonButton>
          )}
        </div>
      ))}
    </div>
  );
};

export default AnnouncementBanner;
